import { UserLanguage } from '../../../../features/account/types/User'
import { LanguageMap } from '../../../../types/LanguageMap'
import { LanguageMaps, LanguageMapsUpdate } from '../../../api/language'
import { ContentLanguageTranslation } from '../../../types/InternalAnalystComment'

export const metadataTagLanguages: UserLanguage[] = ['en', 'ja', 'zh']

export type MetadataTagRow = {
  id: string
  names: ContentLanguageTranslation
}

const metadataTagsOf = (languageMap: LanguageMap | undefined): Record<string, { name?: string }> => languageMap?.metadataTags ?? {}

// A tag with an empty EN name has been deleted; the legacy app never removes the key.
export const buildMetadataTagRows = (languageMaps: LanguageMaps | undefined): MetadataTagRow[] => {
  if (!languageMaps) return []

  return Object.entries(metadataTagsOf(languageMaps.en))
    .filter(([, entry]) => !!entry?.name)
    .map(([id]) => ({
      id,
      names: Object.fromEntries(metadataTagLanguages.map((language) => [language, metadataTagsOf(languageMaps[language])[id]?.name ?? ''])),
    }))
}

export const filterMetadataTagRows = (rows: MetadataTagRow[], search: string): MetadataTagRow[] => {
  const needle = search.trim().toLowerCase()
  return needle ? rows.filter((row) => row.id.toLowerCase().includes(needle)) : rows
}

export const getActiveMetadataTagIds = (rows: MetadataTagRow[]): Set<string> => new Set(rows.map((row) => row.id))

// Language map keys are stored as document field names, which can't contain dots or start with `$`.
export const isValidMetadataTagId = (id: string): boolean => !!id && id === id.trim() && !id.includes('.') && !id.startsWith('$')

export const toMetadataTagUpdate = (id: string, names: ContentLanguageTranslation): LanguageMapsUpdate =>
  Object.fromEntries(metadataTagLanguages.map((language) => [language, { metadataTags: { [id]: { name: names[language] ?? '' } } }]))

// Deleting writes empty names, as the legacy app does.
export const toMetadataTagDeletion = (id: string): LanguageMapsUpdate => toMetadataTagUpdate(id, {})

export const hasMetadataTagNames = (languageMaps: LanguageMaps, id: string, names: ContentLanguageTranslation): boolean =>
  metadataTagLanguages.every((language) => (metadataTagsOf(languageMaps[language])[id]?.name ?? '') === (names[language] ?? ''))
